import React, { useEffect, useState, useRef } from 'react';
import { AccordionBodyItem, AccordionHeaderTitle } from 'component/Accordion';
import { InputComponent } from 'component/Input';
import { TextareaComponent } from 'component/Textarea';
import { RadioButton } from 'component/RadioButton';
import {
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { toast } from 'react-toastify';
import { AccordionBodyItem2 } from '../../../component/Accordion';
import { AsyncSelectComponent } from '../../../component/Select';
import { getImsmaHaRecords } from 'api/HaApi';
import { convertCoords4326To3857 } from 'utils/Helper';
import { getNtsCadastralNumbers } from 'api/NtsApi';

const REASON_LOCAL_REQUEST_INPUT_UA = 'Інформація щодо запиту (Вкажіть номер та дату звернення)';
const REASON_LOCAL_REQUEST_INPUT_EN =
  'Information regarding the request (Please provide the reference number and date)';

const getUtmZoneByLongitude = (longitude) => {
  if (!Number.isFinite(Number(longitude))) return null;
  const lon = Number(longitude);
  if (lon < -180 || lon > 180) return null;
  return Math.floor((lon + 180) / 6) + 1;
};

const extractCadastralNumber = (item) =>
  item?.attributes?.cad_number ??
  item?.attributes?.cadastral_number ??
  item?.cadastral_number ??
  item?.cadastre_number ??
  item?.cadaster_number ??
  item?.number ??
  item?.parcel_number ??
  item?.id ??
  null;

const extractCadastralCentroid = (item) =>
  (Number.isFinite(Number(item?.attributes?.centroid_longitude)) &&
  Number.isFinite(Number(item?.attributes?.centroid_latitude))
    ? [Number(item.attributes.centroid_longitude), Number(item.attributes.centroid_latitude)]
    : null) ??
  (Array.isArray(item?.centroid) ? item?.centroid : null) ??
  item?.centroid_geojson?.coordinates ??
  item?.centroid?.coordinates ??
  item?.centroid_coordinates ??
  item?.coordinates ??
  item?.point?.coordinates ??
  null;

const extractCadastralLocationField = (item, fieldKeys) => {
  const value = fieldKeys
    .map((fieldKey) => item?.[fieldKey] ?? item?.location?.[fieldKey] ?? item?.address?.[fieldKey])
    .find(Boolean);
  return `${value || ''}`.split('/')[0]?.trim();
};

export const NTOResult = (props) => {
  const commentTextareaRef = useRef(null);
  const prevTextareaHeight = useRef(0);
  const [imsmaCentroidById, setImsmaCentroidById] = useState({});
  const [cadastralRecords, setCadastralRecords] = useState([]);
  const [selectedTargetCadastralNumber, setSelectedTargetCadastralNumber] = useState('');

  useEffect(() => {
    const currentReason = props?.formData?.info?.nto_result?.nto_reason;
    const legacyReasonMap = {
      planned_first: 'planned',
      local: 'application',
      feodal_service: 'farmer',
      targeted: 'target',
    };
    const nextReason = legacyReasonMap[currentReason];
    if (!nextReason) return;

    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          nto_reason: nextReason,
          application_nto_reason_comment:
            currentReason === 'local'
              ? prevFormData.info.nto_result.application_nto_reason_comment ||
                prevFormData.info.nto_result.local_nto_request_info ||
                ''
              : prevFormData.info.nto_result.application_nto_reason_comment || '',
          farmer_nto_reason_comment:
            currentReason === 'feodal_service'
              ? prevFormData.info.nto_result.farmer_nto_reason_comment ||
                prevFormData.info.nto_result.feodal_service_request_info ||
                ''
              : prevFormData.info.nto_result.farmer_nto_reason_comment || '',
        },
      },
    }));
  }, [props?.formData?.info?.nto_result?.nto_reason]);

  const isValid4326Coordinate = (coords) =>
    Array.isArray(coords) &&
    coords.length >= 2 &&
    Number.isFinite(Number(coords[0])) &&
    Number.isFinite(Number(coords[1])) &&
    Math.abs(Number(coords[0])) <= 180 &&
    Math.abs(Number(coords[1])) <= 90;

  const handleNTOReason = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          nto_reason: value,
          from_existing_ha_report:
            value === 'repeated' ? prevFormData.info.nto_result.from_existing_ha_report : false,
          hazard_area_id: value === 'repeated' ? prevFormData.info.nto_result.hazard_area_id : null,
          is_update_required:
            value === 'repeated' ? prevFormData.info.nto_result.is_update_required : false,
          ha_imsma_id: value === 'repeated' ? prevFormData.info.nto_result.ha_imsma_id : null,
          application_nto_reason_comment:
            value === 'application'
              ? prevFormData.info.nto_result.application_nto_reason_comment || ''
              : '',
          farmer_nto_reason_comment:
            value === 'farmer' ? prevFormData.info.nto_result.farmer_nto_reason_comment || '' : '',
          target_nto_cadastral_number:
            value === 'target' ? prevFormData.info.nto_result.target_nto_cadastral_number || '' : '',
          target_cadastral_number:
            value === 'target' ? prevFormData.info.nto_result.target_cadastral_number || '' : '',
        },
      },
    }));
  };
  const handleNTOEvidences = (value) => {
    const isEvidencesTrue = value === 'true';
    props.setFormData((prevFormData) => {
      return {
        ...prevFormData,
        info: {
          ...prevFormData.info,
          nto_result: {
            ...prevFormData.info.nto_result,
            evidences: isEvidencesTrue,
            is_already_in_progress: isEvidencesTrue ? null : false,
          },
        },
      };
    });
  };

  const loadHaReportOptions = async (inputValue) => {
    try {
      const response = await getImsmaHaRecords({
        imsmaId: inputValue,
      });
      const rows = response?.data?.results ?? response?.data?.items ?? response?.data ?? [];
      setImsmaCentroidById((prev) => {
        const next = { ...prev };
        rows.forEach((report) => {
          const id =
            report?.imsma_id ??
            report?.imsmaId ??
            report?.info?.general?.imsma_id ??
            report?.properties?.imsma_id;
          const centroid =
            report?.centroid_geojson?.coordinates ??
            report?.coordinates ??
            report?.centroid_coordinates ??
            null;
          if (id && isValid4326Coordinate(centroid)) {
            next[id] = [Number(centroid[0]), Number(centroid[1])];
          }
        });
        return next;
      });
      const uniqueIds = [
        ...new Set(
          rows
            .map(
              (report) =>
                report?.imsma_id ??
                report?.imsmaId ??
                report?.info?.general?.imsma_id ??
                report?.properties?.imsma_id
            )
            .filter(Boolean)
        ),
      ];
      return uniqueIds.map((id) => ({
        value: id,
        label: id,
      }));
    } catch (error) {
      console.error('Error fetching HA reports:', error);
      return [];
    }
  };

  const localCommunityUa = (props?.formData?.info?.location?.local_community || '')
    .split('/')[0]
    ?.trim();
  const selectedRegionUa = (props?.formData?.info?.location?.region || '').split('/')[0]?.trim();
  const selectedDistrictUa = (props?.formData?.info?.location?.district || '')
    .split('/')[0]
    ?.trim();
  const selectedSettlementUa = (props?.formData?.info?.location?.settlement_name || '')
    .split('/')[0]
    ?.trim();

  useEffect(() => {
    const savedTargetNumber =
      props?.formData?.info?.nto_result?.target_nto_cadastral_number ||
      props?.formData?.info?.nto_result?.target_cadastral_number ||
      props?.formData?.info?.nto_result?.targeted_nto_cadastral_number ||
      '';
    setSelectedTargetCadastralNumber(savedTargetNumber);
  }, [
    props?.formData?.info?.nto_result?.target_nto_cadastral_number,
    props?.formData?.info?.nto_result?.target_cadastral_number,
    props?.formData?.info?.nto_result?.targeted_nto_cadastral_number,
  ]);

  useEffect(() => {
    if (props?.formData?.info?.nto_result?.nto_reason !== 'target' || !localCommunityUa) {
      setCadastralRecords([]);
    }
  }, [props?.formData?.info?.nto_result?.nto_reason, localCommunityUa]);

  const targetCadastralOptions = cadastralRecords
    .map((item) => ({
      value: extractCadastralNumber(item),
      label: extractCadastralNumber(item),
      centroid: extractCadastralCentroid(item),
      region: extractCadastralLocationField(item, ['region', 'oblast']),
      district: extractCadastralLocationField(item, ['district', 'raion']),
      localCommunity: extractCadastralLocationField(item, ['local_community', 'hromada']),
      settlement: extractCadastralLocationField(item, [
        'settlement_name',
        'settlement',
        'city',
        'village',
      ]),
    }))
    .filter((item) => item.value)
    .filter(
      (item) =>
        !selectedRegionUa ||
        !item.region ||
        item.region.toLowerCase() === selectedRegionUa.toLowerCase()
    )
    .filter(
      (item) =>
        !selectedDistrictUa ||
        !item.district ||
        item.district.toLowerCase() === selectedDistrictUa.toLowerCase()
    )
    .filter(
      (item) =>
        !localCommunityUa ||
        !item.localCommunity ||
        item.localCommunity.toLowerCase() === localCommunityUa.toLowerCase()
    )
    .filter(
      (item) =>
        !selectedSettlementUa ||
        !item.settlement ||
        item.settlement.toLowerCase() === selectedSettlementUa.toLowerCase()
    );

  const loadTargetCadastralOptions = async (inputValue) => {
    if (props?.formData?.info?.nto_result?.nto_reason !== 'target') return [];
    try {
      const response = await getNtsCadastralNumbers({
        localCommunity: localCommunityUa || undefined,
        search: inputValue,
      });
      const rows =
        response?.data?.results ??
        response?.data?.items ??
        response?.data?.data ??
        response?.data ??
        [];
      const normalizedRows = Array.isArray(rows) ? rows : [];
      setCadastralRecords(normalizedRows);

      const searchValue = `${inputValue || ''}`.trim().toLowerCase();
      const options = normalizedRows
        .map((item) => ({
          value: extractCadastralNumber(item),
          label: extractCadastralNumber(item),
          centroid: extractCadastralCentroid(item),
          region: extractCadastralLocationField(item, ['region', 'oblast']),
          district: extractCadastralLocationField(item, ['district', 'raion']),
          localCommunity: extractCadastralLocationField(item, ['local_community', 'hromada']),
          settlement: extractCadastralLocationField(item, [
            'settlement_name',
            'settlement',
            'city',
            'village',
          ]),
        }))
        .filter((item) => item.value)
        .filter(
          (item) =>
            !selectedRegionUa ||
            !item.region ||
            item.region.toLowerCase() === selectedRegionUa.toLowerCase()
        )
        .filter(
          (item) =>
            !selectedDistrictUa ||
            !item.district ||
            item.district.toLowerCase() === selectedDistrictUa.toLowerCase()
        )
        .filter(
          (item) =>
            !localCommunityUa ||
            !item.localCommunity ||
            item.localCommunity.toLowerCase() === localCommunityUa.toLowerCase()
        )
        .filter(
          (item) =>
            !selectedSettlementUa ||
            !item.settlement ||
            item.settlement.toLowerCase() === selectedSettlementUa.toLowerCase()
        );

      if (!searchValue) return options;
      return options.filter((item) => `${item.label}`.toLowerCase().includes(searchValue));
    } catch (error) {
      console.error('Error fetching cadastral numbers:', error);
      setCadastralRecords([]);
      return [];
    }
  };

  const applyNtoCentroid = (centroid4326, ntoResultPatch = {}) => {
    if (!isValid4326Coordinate(centroid4326)) return;

    const zoneByCentroid = getUtmZoneByLongitude(centroid4326[0]);
    const selectedZone = zoneByCentroid || props?.zone;
    const converted = selectedZone ? convertCoords4326To3857(centroid4326, selectedZone) : null;

    props.setFormData((prevFormData) => ({
      ...prevFormData,
      file_zone: selectedZone || prevFormData.file_zone,
      info: {
        ...prevFormData.info,
        general: {
          ...prevFormData.info.general,
          ...(selectedZone
            ? {
                file_zone: selectedZone,
              }
            : {}),
        },
        nto_result: {
          ...prevFormData.info.nto_result,
          ...ntoResultPatch,
        },
        point: {
          type: 'Point',
          coordinates: [Number(centroid4326[0]), Number(centroid4326[1])],
        },
      },
    }));

    if (
      selectedZone &&
      Array.isArray(converted) &&
      converted.length >= 2 &&
      Number.isFinite(Number(converted[0])) &&
      Number.isFinite(Number(converted[1])) &&
      props?.setCoords3857
    ) {
      props.setCoords3857([Number(converted[0]), Number(converted[1])]);
    }

    if (selectedZone && props?.setZone) {
      props.setZone(selectedZone);
    }
  };

  const notifyHazardCancellation = () => {
    toast.error(
      'Не забудьте створити звіт про відміну небезпечної ділянки / Do not forget to create a report on the cancellation of the hazardous area',
      { autoClose: false }
    );
  };

  const handleNTOVillageByVillage = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          village_by_village: value === 'true' ? true : false,
        },
      },
    }));
  };

  const handleNTOAmmoReport = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          single_ammo_report_needed: value === 'true' ? true : false,
        },
      },
    }));
  };

  const handleNTONewHazard = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          new_hazard_area_report_needed: value === 'true' ? true : false,
        },
      },
    }));
  };

  const handleApplicationNtoReasonComment = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          application_nto_reason_comment: value,
        },
      },
    }));
  };

  const handleFarmerNtoReasonComment = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          farmer_nto_reason_comment: value,
        },
      },
    }));
  };

  const handleNTOComment = (value) => {
    const prevValue = props?.formData?.info?.nto_result?.comment || '';

    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          comment: value,
        },
      },
    }));

    const newLineAdded =
      value.includes('\n') &&
      value.length > prevValue.length &&
      value.split('\n').length > prevValue.split('\n').length;

    const isTypingAtEnd = value.length > prevValue.length && value.startsWith(prevValue);
    const isPageScrolledUp =
      window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 100;

    setTimeout(() => {
      if (commentTextareaRef.current) {
        const textarea = commentTextareaRef.current.querySelector('textarea');
        if (textarea) {
          const currentHeight = textarea.scrollHeight;

          const textareaHeightIncreased = currentHeight > prevTextareaHeight.current;

          if (newLineAdded || textareaHeightIncreased || (isTypingAtEnd && isPageScrolledUp)) {
            textarea.focus();
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);

            const maxScroll = Math.max(
              document.documentElement.scrollHeight,
              document.body.scrollHeight,
              document.documentElement.clientHeight
            );

            window.scrollTo({
              top: maxScroll + 500,
              behavior: 'smooth',
            });

            setTimeout(() => {
              window.scrollTo({
                top: document.documentElement.scrollHeight + 500,
                behavior: 'smooth',
              });
            }, 200);
          }

          prevTextareaHeight.current = currentHeight;
        }
      }
    }, 50);
  };

  const handleIsAlreadyInProgress = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          is_already_in_progress: value === 'true' ? true : false,
        },
      },
    }));
  };

  const handleFromExistingHaReport = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData?.info,
        nto_result: {
          ...prevFormData?.info?.nto_result,
          from_existing_ha_report: value === 'true',
          hazard_area_id: value === 'true' ? prevFormData.info?.nto_result?.hazard_area_id : null,
          is_update_required:
            value === 'true' ? prevFormData.info?.nto_result?.is_update_required : false,
          ha_imsma_id: value === 'true' ? prevFormData.info?.nto_result?.ha_imsma_id : null,
        },
      },
    }));
  };

  const handleIsUpdateRequired = (value) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        nto_result: {
          ...prevFormData.info.nto_result,
          is_update_required: value === 'true',
        },
      },
    }));
  };

  return (
    <>
      <AccordionItemHeading>
        <AccordionItemButton>
          <AccordionHeaderTitle accordionId="3" titleUA="Результат НТО" titleEN="Result of NTO" />
        </AccordionItemButton>
      </AccordionItemHeading>
      <AccordionItemPanel>
        <AccordionBodyItem
          accordionSubId="3.0"
          titleUA="Village by village"
          titleEN="Village by village"
          rightContentClassName="w-40"
          children={
            <div className="grid-columns-2">
              <RadioButton
                label="Так / Yes"
                checked={props.formData.info.nto_result.village_by_village === true}
                id="village_by_village_yes"
                value
                name="village_by_village"
                disabled={false}
                handleInput={handleNTOVillageByVillage}
              />
              <RadioButton
                label="Ні / No"
                checked={props.formData.info.nto_result.village_by_village === false}
                id="village_by_village_no"
                value={false}
                name="village_by_village"
                disabled={false}
                handleInput={handleNTOVillageByVillage}
              />
            </div>
          }
        />
        <AccordionBodyItem
          accordionSubId="3.1"
          titleUA="Причини проведення HTO"
          titleEN="Reasons for performing NTS"
          rightContentClassName="w-65"
          children={
            <div className="flex-column-group">
              <RadioButton
                label={
                  <>
                    <div>
                      Заплановане перше НТО (згідно з Додатком 1 Плану ГР на 2026 - в межах громад,
                      закріплених за оператором)
                    </div>
                    <div>
                      Planned first NTS (according to Appendix 1 of the 2026 AO Plan - within
                      communities assigned to the operator)
                    </div>
                  </>
                }
                checked={props.formData.info.nto_result.nto_reason === 'planned'}
                id="nto_reason_planned"
                value="planned"
                name="nto_reason"
                disabled={false}
                handleInput={handleNTOReason}
              />
              <RadioButton
                label={
                  <>
                    <div>Локальне НТО (за договором, по заявці та ін.)</div>
                    <div>Local NTS (under contract, by request, etc.)</div>
                  </>
                }
                checked={props.formData.info.nto_result.nto_reason === 'application'}
                id="nto_reason_application"
                value="application"
                name="nto_reason"
                disabled={false}
                handleInput={handleNTOReason}
              />
              <RadioButton
                label={
                  <>
                    <div>Повторне НТО</div>
                    <div>Repeated NTS</div>
                  </>
                }
                checked={props.formData.info.nto_result.nto_reason === 'repeated'}
                id="nto_reason_repeated"
                value="repeated"
                name="nto_reason"
                disabled={false}
                handleInput={handleNTOReason}
              />
              <RadioButton
                label={
                  <>
                    <div>Цільове НТО (згідно з Додатком 3 Плану ГР на 2026)</div>
                    <div>Targeted NTS (according to Appendix 3 of the 2026 AO Plan)</div>
                  </>
                }
                checked={props.formData.info.nto_result.nto_reason === 'target'}
                id="nto_reason_target"
                value="target"
                name="nto_reason"
                disabled={false}
                handleInput={handleNTOReason}
              />
              <RadioButton
                label={
                  <>
                    <div>По запиту з сервісу Feodal</div>
                    <div>By request from Feodal service</div>
                  </>
                }
                checked={props.formData.info.nto_result.nto_reason === 'farmer'}
                id="nto_reason_farmer"
                value="farmer"
                name="nto_reason"
                disabled={false}
                handleInput={handleNTOReason}
              />
            </div>
          }
        />
        {props?.formData?.info?.nto_result?.nto_reason === 'application' && (
          <>
            <AccordionBodyItem
              accordionSubId="3.1.1"
              titleUA={REASON_LOCAL_REQUEST_INPUT_UA}
              titleEN={REASON_LOCAL_REQUEST_INPUT_EN}
            />
            <InputComponent
              classNameWrapper="w-100"
              style={{ width: '-webkit-fill-available' }}
              value={props?.formData?.info?.nto_result?.application_nto_reason_comment}
              handleInput={handleApplicationNtoReasonComment}
              placeholder="Description"
              blueLink
              limitChar
            />
          </>
        )}
        {props?.formData?.info?.nto_result?.nto_reason === 'repeated' && (
          <>
            <AccordionBodyItem
              accordionSubId="3.1.1"
              titleUA="Чи це НТО виконується на існуючій небезпечній ділянці?"
              titleEN="Is this NTO performed on an existing hazard area?"
              rightContentClassName="w-40"
              children={
                <div className="grid-columns-2">
                  <RadioButton
                    label="Так / Yes"
                    checked={props?.formData?.info?.nto_result?.from_existing_ha_report}
                    id="from_existing_ha_report_yes"
                    value={true}
                    name="from_existing_ha_report"
                    disabled={false}
                    handleInput={handleFromExistingHaReport}
                  />
                  <RadioButton
                    label="Ні / No"
                    checked={!props?.formData?.info?.nto_result?.from_existing_ha_report}
                    id="from_existing_ha_report_no"
                    value={false}
                    name="from_existing_ha_report"
                    disabled={false}
                    handleInput={handleFromExistingHaReport}
                  />
                </div>
              }
            />
            {props?.formData?.info?.nto_result?.from_existing_ha_report && (
              <>
                <div style={{ width: '64vw' }}>
                  <AccordionBodyItem
                    accordionSubId="3.1.2"
                    titleUA="Оберіть ID небезпечної ділянки I3T / 3T"
                    titleEN="Select the ID of the hazard area I3T / 3T"
                  >
                    <AsyncSelectComponent
                      promiseOptions={loadHaReportOptions}
                      value={
                        props?.formData?.info?.nto_result?.ha_imsma_id
                          ? {
                              value: props.formData.info.nto_result.ha_imsma_id,
                              label: props.formData.info.nto_result.ha_imsma_id,
                            }
                          : null
                      }
                      handleSelect={(selectedValue) => {
                        const selectedImsmaId = selectedValue || null;
                        const centroid4326 = selectedImsmaId
                          ? imsmaCentroidById[selectedImsmaId]
                          : null;
                        if (isValid4326Coordinate(centroid4326)) {
                          applyNtoCentroid(centroid4326, { ha_imsma_id: selectedImsmaId });
                          return;
                        }

                        props.setFormData((prevFormData) => ({
                          ...prevFormData,
                          info: {
                            ...prevFormData.info,
                            nto_result: {
                              ...prevFormData.info.nto_result,
                              ha_imsma_id: selectedImsmaId,
                            },
                          },
                        }));
                      }}
                      placeholder="Select ID I3T / 3T"
                    />
                  </AccordionBodyItem>
                </div>
                <div style={{ width: '64vw' }}>
                  <AccordionBodyItem
                    accordionSubId="3.1.3"
                    titleUA="Широта та довгота небезпечної ділянки"
                    titleEN="Latitude and longitude of the hazard area"
                  >
                    <div className="flex-group">
                      <span style={{ marginRight: '100px', fontWeight: '600', width: '100px' }}>
                        Long:
                        <span style={{ marginLeft: '4px', fontWeight: '500' }}>
                          {props?.coords3857[0] ? props?.coords3857[0]?.toFixed(0) : 0}
                        </span>
                      </span>
                      <span style={{ fontWeight: '600', width: '100px' }}>
                        Lat:
                        <span style={{ marginLeft: '4px', fontWeight: '500' }}>
                          {props?.coords3857[1] ? props?.coords3857[1]?.toFixed(0) : 0}
                        </span>
                      </span>
                    </div>
                  </AccordionBodyItem>
                </div>
                <AccordionBodyItem
                  accordionSubId="3.1.4"
                  titleUA="Чи потребує оновлення існуючий І3Т / 3Т?"
                  titleEN="Does the existing I3T / 3T need to be updated?"
                  rightContentClassName="w-40"
                  children={
                    <div className="grid-columns-2">
                      <RadioButton
                        label="Так / Yes"
                        checked={props?.formData?.info?.nto_result?.is_update_required}
                        id="is_update_required_yes"
                        value={true}
                        name="is_update_required"
                        disabled={false}
                        handleInput={handleIsUpdateRequired}
                      />
                      <RadioButton
                        label="Ні / No"
                        checked={!props?.formData?.info?.nto_result?.is_update_required}
                        id="is_update_required_no"
                        value={false}
                        name="is_update_required"
                        disabled={false}
                        handleInput={handleIsUpdateRequired}
                      />
                    </div>
                  }
                />
                {props?.formData?.info?.nto_result?.is_update_required === true && (
                  <div style={{ width: '69vw' }}>
                    <AccordionBodyItem2
                      titleUA="Зверніться до ЦПМД для внесення змін до ІЗТ / 3Т"
                      titleEN="Contact the CPMD to make changes to the SHA / CHA"
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
        {props?.formData?.info?.nto_result?.nto_reason === 'target' && (
          <div style={{ width: '64vw' }}>
            <AccordionBodyItem
              accordionSubId="3.1.1"
              titleUA="Оберіть кадастровий номер"
              titleEN="Select cadastral number"
            >
              <AsyncSelectComponent
                promiseOptions={loadTargetCadastralOptions}
                value={
                  selectedTargetCadastralNumber
                    ? {
                        value: selectedTargetCadastralNumber,
                        label: selectedTargetCadastralNumber,
                      }
                    : null
                }
                handleSelect={(selectedValue) => {
                  const selectedCadastralNumber = selectedValue || '';
                  setSelectedTargetCadastralNumber(selectedCadastralNumber);
                  const cadastralNumberPatch = {
                    target_nto_cadastral_number: selectedCadastralNumber,
                    target_cadastral_number: selectedCadastralNumber,
                  };
                  const selectedCadastralItem = targetCadastralOptions.find(
                    (item) => item.value === selectedCadastralNumber
                  );
                  const centroid4326 =
                    selectedCadastralItem?.centroid ||
                    extractCadastralCentroid(selectedCadastralItem);

                  if (isValid4326Coordinate(centroid4326)) {
                    applyNtoCentroid(centroid4326, cadastralNumberPatch);
                    return;
                  }

                  props.setFormData((prevFormData) => ({
                    ...prevFormData,
                    info: {
                      ...prevFormData.info,
                      nto_result: {
                        ...prevFormData.info.nto_result,
                        ...cadastralNumberPatch,
                      },
                    },
                  }));
                }}
                placeholder="Select cadastral number"
              />
            </AccordionBodyItem>
          </div>
        )}
        {props?.formData?.info?.nto_result?.nto_reason === 'farmer' && (
          <>
            <AccordionBodyItem
              accordionSubId="3.1.1"
              titleUA={REASON_LOCAL_REQUEST_INPUT_UA}
              titleEN={REASON_LOCAL_REQUEST_INPUT_EN}
            />
            <InputComponent
              classNameWrapper="w-100"
              style={{ width: '-webkit-fill-available' }}
              value={props?.formData?.info?.nto_result?.farmer_nto_reason_comment}
              handleInput={handleFarmerNtoReasonComment}
              placeholder="Description"
              blueLink
              limitChar
            />
          </>
        )}
        <AccordionBodyItem
          accordionSubId="3.2"
          titleUA="Чи виявлені будь-які докази?"
          titleEN="Have any evidence been found?"
          rightContentClassName="w-40"
          children={
            <div className="grid-columns-2">
              <RadioButton
                label="Так / Yes"
                checked={props?.formData?.info?.nto_result?.evidences === true}
                id="evidences_yes"
                value
                name="evidences"
                disabled={false}
                handleInput={handleNTOEvidences}
              />
              <RadioButton
                label="Ні / No"
                checked={props?.formData?.info?.nto_result?.evidences === false}
                id="evidences_no"
                value={false}
                name="evidences"
                disabled={false}
                handleInput={handleNTOEvidences}
              />
            </div>
          }
        />
        {props?.formData?.info?.nto_result?.evidences === false && (
          <AccordionBodyItem
            accordionSubId="3.2.1"
            titleUA="Чи це НТО виконується на існуючій небезпечній ділянці і вона потребує відміни?"
            titleEN="Is this non-routine operation being performed in an existing hazardous area, and does it require cancellation?"
            rightContentClassName="w-40"
            children={
              <div className="grid-columns-2">
                <RadioButton
                  label="Так / Yes"
                  checked={props?.formData?.info?.nto_result?.is_already_in_progress === true}
                  id="is_already_in_progress_yes"
                  value
                  name="is_already_in_progress"
                  disabled={false}
                  handleInput={(value) => {
                    handleIsAlreadyInProgress(value);
                    if (value) notifyHazardCancellation();
                  }}
                />
                <RadioButton
                  label="Ні / No"
                  checked={props?.formData?.info?.nto_result?.is_already_in_progress === false}
                  id="is_already_in_progress_no"
                  value={false}
                  name="is_already_in_progress"
                  disabled={false}
                  handleInput={handleIsAlreadyInProgress}
                />
              </div>
            }
          />
        )}
        <AccordionBodyItem
          accordionSubId="3.3"
          titleUA="Чи має бути створено звіт про виявлення одиничного боєприпасу?"
          titleEN="Should a report be created for the detection of a single ordnance?"
          rightContentClassName="w-40"
          children={
            <div className="grid-columns-2">
              <RadioButton
                label="Так / Yes"
                checked={props.formData.info.nto_result.single_ammo_report_needed === true}
                id="ammo_report_yes"
                value
                name="single_ammo_report_needed"
                disabled={false}
                handleInput={handleNTOAmmoReport}
              />
              <RadioButton
                label="Ні / No"
                checked={props.formData.info.nto_result.single_ammo_report_needed === false}
                id="ammo_report_no"
                value={false}
                name="single_ammo_report_needed"
                disabled={false}
                handleInput={handleNTOAmmoReport}
              />
            </div>
          }
        />
        <AccordionBodyItem
          accordionSubId="3.4"
          titleUA="Чи має бути створено нову небезпечну територію?"
          titleEN="Should a new hazardous area be established?"
          rightContentClassName="w-40"
          children={
            <div className="grid-columns-2">
              <RadioButton
                label="Так / Yes"
                checked={props.formData.info.nto_result.new_hazard_area_report_needed === true}
                id="new_hazard_yes"
                value
                name="new_hazard_area_report_needed"
                disabled={false}
                handleInput={handleNTONewHazard}
              />
              <RadioButton
                label="Ні / No"
                checked={props.formData.info.nto_result.new_hazard_area_report_needed === false}
                id="new_hazard_no"
                value={false}
                name="new_hazard_area_report_needed"
                disabled={false}
                handleInput={handleNTONewHazard}
              />
            </div>
          }
        />
        <AccordionBodyItem accordionSubId="3.5" titleUA="Коментар" titleEN="Comment" />
        <div ref={commentTextareaRef}>
          <TextareaComponent
            className="w-100"
            style={{ width: '-webkit-fill-available' }}
            value={props?.formData?.info?.nto_result?.comment}
            handleTextarea={handleNTOComment}
            id="nto-comment"
            name="nto-comment"
            limitChar
            blueLink
            maxLength={5000}
          />
        </div>
      </AccordionItemPanel>
    </>
  );
};
